import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router'
import { routes } from 'types/routes'
import { useStoreSelector } from 'hooks/store'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { PageTab } from 'layouts/page-layout/types.ts'
import EditLayout from 'layouts/edit-layout/EditLayout.tsx'
import useGetRouteLayout from 'hooks/useGetRouteLayout.ts'
import { Layouts } from 'layouts/types.ts'
import { FaqSlugB2B, FaqSlugB2C } from 'types/faq.ts'
import { useNavigate } from 'react-router'
import { Button, popErrorToast } from '@boltenergy-be/design-system'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import { Methods, Response } from 'types/request.ts'
import { GetContractConsumptionsResponseBody } from 'lib/types.ts'
import { selectCurrentServiceContracts, selectIsProducerAccount } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { hasEnergyTypeValue } from 'store/access-rights/utils.ts'
import { SPOTLIGHT_STORAGE_KEY } from './constants.ts'
import { selectIsSuperUser } from 'store/auth/slice.ts'
import Spotlight from 'components/spotlight/Spotlight.tsx'

const Consumption = () => {
  // Redux
  const isSuperUser = useStoreSelector(selectIsSuperUser)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)

  // Access rights
  const { canAccess } = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)
  const { historic } = useGetAccessRight(ACCESS_RIGHT_KEYS.marketPrices)

  // Local state
  const [downloadingCsv, setDownloadingCsv] = useState<boolean>(false)
  const [downloadError, setDownloadError] = useState<boolean>(false)
  const [spotlightDismissed, setSpotlightDismissed] = useState<boolean>(localStorage.getItem(SPOTLIGHT_STORAGE_KEY) === 'closed')
  const [spotlightAnchor, setSpotlightAnchor] = useState<HTMLAnchorElement | HTMLButtonElement | null>(null)

  // i18n
  const { t } = useTranslation(['consumption', 'common'])

  // Layout
  const { layout } = useGetRouteLayout()

  // Router
  const { pathname } = useLocation()
  const navigate = useNavigate()

  /**
   * Close the spotlight popover and store the state in localStorage to prevent it from showing again
   *  TODO: will be refactored to event that will be logged instead of localStorage
   */
  const hideSpotlight = () => {
    setSpotlightDismissed(true)
    localStorage.setItem(SPOTLIGHT_STORAGE_KEY, 'closed')
  }

  // Constants
  const canShowSpotlight = !isSuperUser && !spotlightDismissed
  const tabs = useMemo<PageTab[]>(() => {
    const tabs: PageTab[] = [
      {
        label: t(($) => $.electricity, {
          ns: 'common'
        }),
        path: routes.CONSUMPTION_ELECTRICITY
      }
    ]

    if (gas) {
      tabs.push({
        label: t(($) => $.gas, {
          ns: 'common'
        }),
        path: routes.CONSUMPTION_GAS
      })
    }

    if (hasEnergyTypeValue(canAccess.view)) {
      tabs.push({ label: t(($) => $.tabs.meterReadings), path: routes.CONSUMPTION_METER_READINGS })
    }

    if (historic.canAccess) {
      tabs.push({
        label: t(($) => $.tabs.priceHistory),
        path: routes.CONSUMPTION_PRICE_HISTORY,
        anchorRef: setSpotlightAnchor,
        onClick: canShowSpotlight ? hideSpotlight : undefined
      })
    }

    return tabs
  }, [canAccess.view, canShowSpotlight, gas, historic.canAccess, t])
  const hasPriceHistoryTab = tabs.some((tab) => tab.path === routes.CONSUMPTION_PRICE_HISTORY)

  const downloadCsv = async () => {
    setDownloadingCsv(true)

    try {
      const {
        success,
        data: responseData,
        error,
        message
      }: Response<GetContractConsumptionsResponseBody> = await newRequest(
        getAxiosRequestConfig(Methods.GET, `/contracts/${electricity.contractNumber}/consumptions-csv?as=producer`)
      )

      if (success && responseData) {
        // Download the file
        const blob = new Blob([responseData.consumptionsCsv], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `contract-${electricity.deliveryPoint.ean}.csv`
        a.click()
        window.URL.revokeObjectURL(url)
      }

      if (error) {
        setDownloadError(true)
        popErrorToast(t(($) => $.yourConsumption.download.error))
        log({ error: message || 'unhandled error', identifier: `[${Flow.CONTRACTS}:downloadCsv]` })
      }
    } catch (err) {
      setDownloadError(true)
      popErrorToast(t(($) => $.yourConsumption.download.error))
      log({ error: err as Error, identifier: `[${Flow.CONTRACTS}:downloadCsv]` })
    } finally {
      setDownloadingCsv(false)
    }
  }

  const ACTIONS: { [key: string]: ReactNode[] } = {
    [routes.CONSUMPTION_ELECTRICITY]: isProducer
      ? [
          <Button
            key="csv-download"
            leadingIcon="download"
            variant="tertiary"
            disabled={downloadError}
            loading={downloadingCsv}
            onClick={async () => {
              await downloadCsv()
              trackEvent({ category: 'consumption', action: 'click', object: 'download_csv' })
            }}
          />
        ]
      : [],
    [routes.CONSUMPTION_GAS]: [],
    [routes.CONSUMPTION_METER_READINGS]: []
  }

  // Redirect to electricity usage if the user lands on the general consumption route
  useEffect(() => {
    if (pathname === routes.CONSUMPTION) {
      void navigate(routes.CONSUMPTION_ELECTRICITY, { replace: true })
    }
  }, [pathname, navigate])

  return layout === Layouts.EDIT_LAYOUT ? (
    <EditLayout title={t(($) => $.editMeterReadings.title)}>
      <Outlet />
    </EditLayout>
  ) : (
    <PageLayout
      {...{ tabs }}
      title={t(($) => $.nav[isProducer ? 'injection' : 'usage'], {
        ns: 'common'
      })}
      description={
        pathname.includes(routes.CONSUMPTION_METER_READINGS) && hasEnergyTypeValue(canAccess.edit)
          ? t(($) => $.meterReadings.description, {
              ns: 'consumption'
            })
          : undefined
      }
      faqCategories={{ b2c: [FaqSlugB2C.CONSUMPTION], b2b: [FaqSlugB2B.INJECTION] }}
      actions={ACTIONS[pathname]}
    >
      <Outlet />

      {hasPriceHistoryTab && (
        <Spotlight open={canShowSpotlight} anchorElement={spotlightAnchor} placement="bottom-end" onClose={hideSpotlight}>
          <p>{t(($) => $.priceHistory.spotlight)}</p>
        </Spotlight>
      )}
    </PageLayout>
  )
}

export default Consumption
